import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, useRef } from "react";

interface IBarcodeValidationModalProps {
  isOpen: boolean;
  itemName?: string;
  barcodeValue: string;
  showMismatchError?: boolean;
  onChangeBarcode: (value: string) => void;
  onClose: () => void;
  onConfirm?: () => void;
}

const BarcodeValidationModal: React.FC<IBarcodeValidationModalProps> = ({
  isOpen,
  itemName,
  barcodeValue,
  showMismatchError,
  onChangeBarcode,
  onClose,
  onConfirm,
}) => {
  const barcodeInputRef = useRef<HTMLInputElement>(null);

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-40" onClose={onClose} initialFocus={barcodeInputRef}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/70 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg border border-gray-200 bg-white px-5 pb-5 pt-6 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-xl sm:p-7">
                <div className="flex items-center justify-between">
                  <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                    Scan barcode to add this item to cart
                  </Dialog.Title>
                  <button type="button" className="text-gray-500 hover:text-gray-700" onClick={onClose}>
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="mt-3">
                  {itemName ? <p className="mt-1 text-xs font-medium text-gray-500">{itemName}</p> : null}
                  <input
                    ref={barcodeInputRef}
                    type="text"
                    value={barcodeValue}
                    onChange={(event) => onChangeBarcode(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && onConfirm) {
                        onConfirm();
                      }
                    }}
                    placeholder="Scan or type barcode"
                    className="mt-2 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-[#111827] placeholder:text-[#9ca3af] caret-black focus:border-gray-400 focus:outline-none focus:ring-0"
                  />
                  {showMismatchError ? <p className="mt-2 text-xs font-medium text-red-600">Barcode Mismatch</p> : null}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default BarcodeValidationModal;
